import { useEffect, useMemo, useState } from 'react'
import { DashboardPageHeader } from '../DashboardPageHeader'
import {
  publicSiteUrl,
  getAdminUser,
  listAdminUsers,
  updateUserRole,
  type AdminUserDetail,
  type AdminUserRow,
  type UserRole,
} from '../../lib/api'
import { IconChevronLeft, IconExternalLink, IconLoader2, IconSearch, IconShieldCheck , IconWorld} from '@tabler/icons-react'
import { editorHref } from '../../lib/editorSession'
import { activeLanguage, useI18n } from '../../lib/i18n'
import type { Translate } from '@webbuilder/i18n'
import { matchesSearch } from '@webbuilder/shared-types'
import { useSearchPlaceholder } from '../../hooks/useSearchPlaceholder'

/* Claves, no texto: constante de módulo. */
const ROLE_LABEL: Record<UserRole, string> = {
  user: 'admin.users.roleHintUser',
  designer: 'admin.users.roleHintDesigner',
  admin: 'admin.users.roleHintAdmin',
}
const ROLE_ORDER: UserRole[] = ['user', 'designer', 'admin']

/**
 * «hace 3 días», «hace 2 meses», «Nunca».
 *
 * Una fecha suelta obliga a restarla mentalmente para saber si esa cuenta sigue
 * activa, que es la única pregunta que se le hace a esta columna.
 */
function fmtSince(value: string | null, t: Translate): string {
  if (!value) return t('admin.users.never')
  const stamp = new Date(value).getTime()
  if (Number.isNaN(stamp)) return t('common.none')
  const minutes = Math.round((Date.now() - stamp) / 60_000)
  if (minutes < 2) return t('admin.users.now')
  if (minutes < 60) return t('admin.users.agoMin', { count: minutes })
  const hours = Math.round(minutes / 60)
  if (hours < 24) return t('admin.users.agoHours', { count: hours })
  const days = Math.round(hours / 24)
  if (days < 31) return t(days === 1 ? 'admin.users.agoDayOne' : 'admin.users.agoDayMany', { count: days })
  const months = Math.round(days / 30)
  if (months < 12) return t(months === 1 ? 'admin.users.agoMonthOne' : 'admin.users.agoMonthMany', { count: months })
  const years = Math.round(months / 12)
  return t(years === 1 ? 'admin.users.agoYearOne' : 'admin.users.agoYearMany', { count: years })
}

function fmtDate(value: string | null): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleDateString(activeLanguage(), { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return '—'
  }
}

export function AdminUsers({ token }: { token: string }) {
  const { t } = useI18n()
  const searchPlaceholder = useSearchPlaceholder('admin.users.search')
  const [rows, setRows] = useState<AdminUserRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    listAdminUsers(token)
      .then((r) => { if (alive) setRows(r) })
      .catch(() => { if (alive) setError('admin.users.loadFailed') })
    return () => { alive = false }
  }, [token])

  const filtered = useMemo(() => {
    if (!rows) return []
    const q = query.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((u) => matchesSearch(u.email, q) || u.id.includes(q))
  }, [rows, query])

  if (selectedId) {
    return (
      <AdminUserDetailView
        token={token}
        id={selectedId}
        onBack={() => setSelectedId(null)}
        onRoleChange={(id, role) => setRows((prev) => prev?.map((u) => (u.id === id ? { ...u, role } : u)) ?? prev)}
      />
    )
  }

  return (
    <div className="dash-dashboard-view dash-standard-page">
      <DashboardPageHeader
        title={t('admin.users.title')}
        subtitle={t('admin.users.subtitle')}
      />

      <div className="dash-admin-users-toolbar">
        <div className="dash-admin-users-search">
          <IconSearch size={16} />
          <input
            type="search"
            placeholder={searchPlaceholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {rows ? <span className="dash-admin-users-count">{t('admin.users.count', { shown: filtered.length, total: rows.length })}</span> : null}
      </div>

      {error ? <p className="dash-pricing-error" role="alert">{errorText(error, t)}</p> : null}

      {!rows ? (
        <p className="dash-admin-users-empty"><IconLoader2 size={15} className="dash-spin" /> {t('admin.users.loading')}</p>
      ) : filtered.length === 0 ? (
        <p className="dash-admin-users-empty">{t('admin.users.none')}</p>
      ) : (
        <div className="dash-admin-users-table">
          <div className="dash-admin-users-row dash-admin-users-head">
            <span>{t('admin.users.col.user')}</span>
            <span>{t('admin.users.col.role')}</span>
            <span>{t('admin.users.col.plan')}</span>
            <span>{t('admin.users.col.projects')}</span>
            <span>{t('admin.users.col.lastSeen')}</span>
            <span>{t('admin.users.col.signup')}</span>
          </div>
          {filtered.map((u) => (
            <button key={u.id} type="button" className="dash-admin-users-row" onClick={() => setSelectedId(u.id)}>
              <span className="dash-admin-users-email">{u.email ?? t('admin.users.noEmail')}</span>
              <span><RoleBadge role={u.role} superadmin={u.isSuperadmin} /></span>
              <span className={`dash-admin-plan is-${u.plan}`}>{u.plan === 'free' ? 'Free' : u.plan === 'pro' ? 'Pro' : 'Business'}</span>
              <span>{u.projectCount}</span>
              {/* Cuánto hace que entró, no la fecha exacta: lo que se busca en
                  esta lista es quién sigue vivo y quién no ha vuelto, y «hace 3
                  días» responde eso de un vistazo. La fecha completa está en el
                  título al pasar por encima. */}
              <span
                className={`dash-admin-users-date${u.lastSignInAt ? '' : ' is-never'}`}
                title={u.lastSignInAt ? fmtDate(u.lastSignInAt) : t('admin.users.neverIn')}
              >
                {fmtSince(u.lastSignInAt, t)}
              </span>
              <span className="dash-admin-users-date">{fmtDate(u.createdAt)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * El aviso, venga de donde venga.
 *
 * Los nuestros se guardan como CLAVE porque se ponen dentro de un efecto, y
 * traducir ahí ataría la carga de datos al idioma: cambiarlo volvería a pedirle
 * la lista al servidor. Los del servidor llegan ya escritos y salen tal cual.
 */
function errorText(value: string, t: Translate): string {
  return value.startsWith('admin.users.') ? t(value) : value
}

function RoleBadge({ role, superadmin }: { role: UserRole; superadmin?: boolean }) {
  const { t } = useI18n()
  return (
    <span className={`dash-role-badge is-${role}`}>
      {superadmin ? <IconShieldCheck size={12} /> : null}
      {t(ROLE_LABEL[role])}
    </span>
  )
}

function AdminUserDetailView({
  token, id, onBack, onRoleChange,
}: {
  token: string
  id: string
  onBack: () => void
  onRoleChange: (id: string, role: UserRole) => void
}) {
  const { t } = useI18n()
  const [detail, setDetail] = useState<AdminUserDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [role, setRole] = useState<UserRole>('user')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    let alive = true
    getAdminUser(token, id)
      .then((d) => { if (alive) { setDetail(d); setRole(d.role) } })
      .catch(() => { if (alive) setError('admin.users.loadOneFailed') })
    return () => { alive = false }
  }, [token, id])

  const dirty = detail ? role !== detail.role : false
  const locked = detail?.isSuperadmin ?? false

  const handleSave = async () => {
    if (!dirty || saving) return
    setError(null)
    setSaving(true)
    try {
      await updateUserRole(token, id, role)
      setDetail((prev) => (prev ? { ...prev, role } : prev))
      onRoleChange(id, role)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch (e) {
      setError(e instanceof Error ? e.message.replace(/^\d+:\s*/, '') : t('admin.users.roleFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="dash-dashboard-view dash-standard-page">
      <button type="button" className="dash-admin-back" onClick={onBack}><IconChevronLeft size={16} /> {t('admin.users.back')}</button>

      {error ? <p className="dash-pricing-error" role="alert">{errorText(error, t)}</p> : null}

      {!detail ? (
        <p className="dash-admin-users-empty"><IconLoader2 size={15} className="dash-spin" /> {t('admin.users.loadingOne')}</p>
      ) : (
        <>
          <DashboardPageHeader title={detail.email ?? t('admin.users.noEmail')} subtitle={t('admin.users.registeredOn', { date: fmtDate(detail.createdAt) })} />

          <div className="dash-admin-detail-grid">
            <section className="dash-admin-card">
              <h3>{t('admin.users.role')}</h3>
              {/* Los tres nombres van en negrita, así que la frase se parte. */}
              <p className="dash-admin-card-hint">
                <strong>{t('admin.users.roleHintAdmin')}</strong>{t('admin.users.roleHint1')}
                <strong>{t('admin.users.roleHintDesigner')}</strong>{t('admin.users.roleHint2')}
                <strong>{t('admin.users.roleHintUser')}</strong>{t('admin.users.roleHint3')}
              </p>
              <div className="dash-admin-role-row">
                <select className="dash-form-control" value={role} disabled={locked} onChange={(e) => setRole(e.target.value as UserRole)}>
                  {ROLE_ORDER.map((r) => <option key={r} value={r}>{t(ROLE_LABEL[r])}</option>)}
                </select>
                <button type="button" className="dash-primary-btn" disabled={!dirty || saving || locked} onClick={() => void handleSave()}>
                  {saving ? <><IconLoader2 size={14} className="dash-spin" /> {t('admin.users.saving')}</> : t(saved ? 'admin.users.savedMark' : 'admin.users.save')}
                </button>
              </div>
              {locked ? <p className="dash-admin-card-note">{t('admin.users.locked')}</p> : null}
            </section>

            <section className="dash-admin-card">
              <h3>{t('admin.users.subscription')}</h3>
              <dl className="dash-admin-dl">
                <div><dt>{t('admin.users.col.plan')}</dt><dd className={`dash-admin-plan is-${detail.subscription.plan}`}>{detail.subscription.plan === 'free' ? 'Free' : detail.subscription.plan === 'pro' ? 'Pro' : 'Business'}</dd></div>
                <div><dt>{t('admin.users.status')}</dt><dd>{detail.subscription.status}</dd></div>
                <div><dt>{t('admin.users.renews')}</dt><dd>{fmtDate(detail.subscription.currentPeriodEnd)}</dd></div>
                <div><dt>{t('admin.users.stripeCustomer')}</dt><dd className="dash-admin-mono">{detail.subscription.stripeCustomerId ?? t('common.none')}</dd></div>
              </dl>
            </section>
          </div>

          <section className="dash-admin-card">
            <h3>{t('admin.users.projects', { count: detail.projects.length })}</h3>
            {detail.projects.length === 0 ? (
              <p className="dash-admin-card-note">{t('admin.users.noProjects')}</p>
            ) : (
              <div className="dash-admin-projects">
                {detail.projects.map((p) => {
                  /* El dominio propio manda sobre el de pruebas: es la dirección
                     donde el sitio vive de verdad. */
                  const siteUrl = p.primaryDomain
                    ? `https://${p.primaryDomain}`
                    : p.subdomain ? publicSiteUrl(p.subdomain) : null
                  return (
                  <div key={p.id} className="dash-admin-project">
                    {/* La captura del sitio, no sólo su nombre: revisando la
                        cuenta de alguien, ver cómo le quedó dice más que una
                        fecha. Lleva al sitio publicado cuando lo hay. */}
                    {siteUrl && p.publishedAt ? (
                      <a
                        className="dash-admin-project-thumb"
                        href={siteUrl}
                        target="_blank"
                        rel="noreferrer"
                        title={t('admin.users.viewPublished', { name: p.name })}
                      >
                        {p.thumbnailUrl
                          ? <img src={p.thumbnailUrl} alt="" loading="lazy" decoding="async" width={1200} height={1500} />
                          : <span className="dash-admin-project-thumb-empty"><IconWorld size={18} /></span>}
                      </a>
                    ) : (
                      <span className="dash-admin-project-thumb is-draft" aria-hidden="true">
                        <span className="dash-admin-project-thumb-empty"><IconWorld size={18} /></span>
                      </span>
                    )}
                    <div className="dash-admin-project-main">
                      <strong>{p.name}</strong>
                      <span className="dash-admin-project-meta">
                        {t(p.publishedAt ? 'admin.users.published' : 'admin.users.draft')}{t('admin.users.updated', { date: fmtDate(p.updatedAt) })}
                      </span>
                      {p.primaryDomain ? <span className="dash-admin-project-meta">{p.primaryDomain}</span> : null}
                    </div>
                    <div className="dash-admin-project-links">
                      <a href={editorHref(`?pid=${p.id}`)} target="_blank" rel="noreferrer">{t('admin.users.editor')} <IconExternalLink size={12} /></a>
                      {p.publishedAt && siteUrl ? (
                        <a href={siteUrl} target="_blank" rel="noreferrer">{t('admin.users.site')} <IconExternalLink size={12} /></a>
                      ) : null}
                    </div>
                  </div>
                  )
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  )
}
